import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCcw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { handleError } from "@/lib/errorHandler";
import { captureException } from "@/integrations/posthog/client";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Per-route error boundary.
 * Catches crashes inside a single page/route and shows a recovery UI
 * without unmounting the entire app (Layout, nav, providers stay alive).
 */
class RouteErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    handleError(error, {
      context: "route_crash",
      category: "crash",
      details: { componentStack: errorInfo.componentStack?.substring(0, 2000) },
    });
    captureException(error, {
      context: "route_crash",
      componentStack: errorInfo.componentStack?.substring(0, 2000),
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  public render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        className="flex-1 flex items-center justify-center px-6 py-12"
        dir="rtl"
      >
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>

          <h2 className="text-xl font-bold text-foreground mb-2">
            הדף נתקל בבעיה
          </h2>

          <p className="text-sm text-muted-foreground mb-6">
            ניתן לנסות שוב או לחזור לדף הבית
          </p>

          <div className="flex flex-col gap-2">
            <Button onClick={this.handleRetry} size="lg" className="w-full">
              <RefreshCcw className="w-4 h-4 ml-2" />
              לנסות שוב
            </Button>

            <Button
              onClick={this.handleGoHome}
              variant="outline"
              size="lg"
              className="w-full"
            >
              <Home className="w-4 h-4 ml-2" />
              דף הבית
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
